// The SOL value of a listed LST balance (docs/19-staking-plan.md sections 3.4 item 3 and 3.6): the rate comes from the
// pool's own on-chain state, read over the app's RPC (no third-party price or APY service; docs/08 talks to exactly
// two services). JitoSOL and JupSOL: the SPL stake pool's total_lamports / pool_token_supply; mSOL: Marinade's
// msol_price; INF: Infinity's total_sol_value over the INF supply, an estimate (its layout was read by us, not checked
// against Sanctum's IDL). The byte readers and pool addresses live in the SDK (sdk/ts/src/lst.ts, tested there on the
// pool accounts pinned in sdk/fixtures/jupiter-lst/).
import { type LstPool, type LstRate, lstPoolOf, lstRate, lstRateAccounts, lstSolValue } from '@thevault/sdk';
import type { Rpc } from './rpc';

export interface LstValue {
  pool: LstPool;
  /** Lamports the balance is worth at the pool's rate (rounded down). */
  lamports: bigint;
  rate: LstRate;
  /** The context slot of the read. */
  slot: number;
  /** True for INF (see above); every value is indicative. */
  estimate: boolean;
}

/** Whether a mint is a listed LST whose value can be read. */
export function isListedLst(mint: string): boolean {
  return !!lstPoolOf(mint);
}

/** The SOL value of `amount` base units of a listed LST, or null for any other mint. Throws when the pool cannot be read. */
export async function readLstValue(rpc: Pick<Rpc, 'getMultipleAccounts'>, mint: string, amount: bigint): Promise<LstValue | null> {
  const pool = lstPoolOf(mint);
  if (!pool) return null;
  const read = await rpc.getMultipleAccounts(lstRateAccounts(pool));
  const rate = lstRate(pool, read.accounts);
  return { pool, lamports: lstSolValue(amount, rate), rate, slot: read.slot, estimate: pool.estimate };
}
